import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Wrench, Plus, AlertTriangle, Clock, CheckCircle2, XCircle,
  Calendar, User, Building2, ChevronRight, RefreshCw, Filter,
  AlertOctagon, Timer, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const REPAIR_CATEGORIES = [
  { id: 'plumbing', label: 'Plumbing', icon: '🔧' },
  { id: 'electrical', label: 'Electrical', icon: '⚡' },
  { id: 'heating', label: 'Heating/HVAC', icon: '🔥' },
  { id: 'structural', label: 'Structural', icon: '🏠' },
  { id: 'damp', label: 'Damp/Mould', icon: '💧' },
  { id: 'pest', label: 'Pest Control', icon: '🐛' },
  { id: 'security', label: 'Security/Locks', icon: '🔒' },
  { id: 'appliances', label: 'Appliances', icon: '🔌' },
  { id: 'other', label: 'Other', icon: '📋' }
];

const PRIORITIES = [
  { id: 'emergency', label: 'Emergency', color: 'bg-red-500', description: '24 hours' },
  { id: 'urgent', label: 'Urgent', color: 'bg-orange-500', description: '48 hours' },
  { id: 'routine', label: 'Routine', color: 'bg-blue-500', description: '28 days' },
  { id: 'planned', label: 'Planned', color: 'bg-gray-500', description: '90 days' }
];

export default function RepairsSLAPage() {
  const [repairs, setRepairs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    sla_breached: ''
  });
  const [formData, setFormData] = useState({
    property_id: '',
    title: '',
    description: '',
    category: '',
    location_in_property: '',
    priority: 'routine',
    is_hazard: false,
    hazard_category: '',
    hazard_type: ''
  });

  const fetchRepairs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.sla_breached) params.append('sla_breached', filters.sla_breached === 'true');
      
      const response = await api.get(`/prs-services/repairs?${params}`);
      setRepairs(response.data.repairs);
    } catch (error) {
      toast.error('Failed to load repairs');
    }
  }, [filters]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/prs-services/repairs/dashboard');
      setDashboard(response.data.dashboard);
    } catch (error) {
      console.error('Failed to load dashboard');
    }
  };

  useEffect(() => {
    Promise.all([fetchRepairs(), fetchDashboard()]).finally(() => setLoading(false));
  }, [fetchRepairs]);

  const fetchRepairDetails = async (repairId) => {
    try {
      const response = await api.get(`/prs-services/repairs/${repairId}`);
      setSelectedRepair(response.data.repair);
    } catch (error) {
      toast.error('Failed to load repair details');
    }
  };

  const handleCreateRepair = async (e) => {
    e.preventDefault();
    try {
      await api.post('/prs-services/repairs', formData);
      toast.success('Repair request created');
      setShowCreateForm(false);
      fetchRepairs();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create repair request');
    }
  };

  const handleAcknowledge = async (repairId) => {
    try {
      await api.post('/prs-services/repairs/acknowledge', { repair_id: repairId });
      toast.success('Repair acknowledged');
      fetchRepairDetails(repairId);
      fetchRepairs();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to acknowledge repair');
    }
  };

  const handleSchedule = async (repairId) => {
    const date = prompt('Enter scheduled date (YYYY-MM-DD):');
    if (!date) return;
    
    try {
      await api.post('/prs-services/repairs/schedule', {
        repair_id: repairId,
        scheduled_date: date,
        scheduled_time_slot: 'morning'
      });
      toast.success('Repair scheduled');
      fetchRepairDetails(repairId);
      fetchRepairs();
    } catch (error) {
      toast.error('Failed to schedule repair');
    }
  };

  const handleComplete = async (repairId) => {
    const notes = prompt('Enter completion notes:');
    if (!notes) return;
    
    try {
      await api.post('/prs-services/repairs/complete', {
        repair_id: repairId,
        completion_notes: notes
      });
      toast.success('Repair marked as completed');
      fetchRepairDetails(repairId);
      fetchRepairs();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to complete repair');
    }
  };

  const checkSLABreaches = async () => {
    try {
      const response = await api.post('/prs-services/repairs/check-sla-breaches');
      if (response.data.breached_count > 0) {
        toast.warning(`${response.data.breached_count} repairs have breached SLA`);
      } else {
        toast.success('No SLA breaches found');
      }
      fetchRepairs();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to check SLA breaches');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      reported: 'bg-yellow-100 text-yellow-800',
      acknowledged: 'bg-blue-100 text-blue-800',
      scheduled: 'bg-indigo-100 text-indigo-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      verified: 'bg-emerald-100 text-emerald-800',
      disputed: 'bg-red-100 text-red-800',
      escalated: 'bg-orange-100 text-orange-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.id === priority);
    return p ? <Badge className={`${p.color} text-white`}>{p.label}</Badge> : null;
  };

  const getSLAProgress = (repair) => {
    if (!repair.sla_deadline) return 0;
    const now = new Date();
    const deadline = new Date(repair.sla_deadline);
    const reported = new Date(repair.reported_at);
    const total = deadline - reported;
    const elapsed = now - reported;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="repairs-sla-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Repairs & SLA Tracking</h1>
            <p className="text-slate-600">HHSRS compliant repair management with SLA monitoring</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={checkSLABreaches}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Check SLA
            </Button>
            <Button onClick={() => setShowCreateForm(true)} data-testid="create-repair-btn">
              <Plus className="h-4 w-4 mr-2" />
              Report Repair
            </Button>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Repairs</p>
                    <p className="text-2xl font-bold">{dashboard.total_repairs}</p>
                  </div>
                  <Wrench className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Open Repairs</p>
                    <p className="text-2xl font-bold">{dashboard.open_repairs}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">SLA Breached</p>
                    <p className="text-2xl font-bold text-red-600">{dashboard.sla_breached}</p>
                  </div>
                  <AlertOctagon className="h-8 w-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{dashboard.completed_repairs}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {showCreateForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Report New Repair</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRepair} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Property ID"
                    value={formData.property_id}
                    onChange={(e) => setFormData({...formData, property_id: e.target.value})}
                    required
                  />
                  <select
                    className="border rounded-md p-2"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    <option value="">Select Category...</option>
                    {REPAIR_CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>

                <Input
                  placeholder="Repair Title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />

                <Textarea
                  placeholder="Describe the issue in detail..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Location in Property"
                    value={formData.location_in_property}
                    onChange={(e) => setFormData({...formData, location_in_property: e.target.value})}
                  />
                  <select
                    className="border rounded-md p-2"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.id} value={p.id}>{p.label} - {p.description}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_hazard}
                      onChange={(e) => setFormData({...formData, is_hazard: e.target.checked})}
                    />
                    <span>HHSRS Hazard</span>
                  </label>
                  {formData.is_hazard && (
                    <select
                      className="border rounded-md p-2"
                      value={formData.hazard_category}
                      onChange={(e) => setFormData({...formData, hazard_category: e.target.value})}
                    >
                      <option value="">Hazard Category...</option>
                      <option value="category_1">Category 1 (Serious)</option>
                      <option value="category_2">Category 2 (Other)</option>
                    </select>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="submit">Submit Repair Request</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-slate-400" />
              <select
                className="border rounded-md p-2"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Statuses</option>
                <option value="reported">Reported</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="escalated">Escalated</option>
              </select>
              <select
                className="border rounded-md p-2"
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
              >
                <option value="">All Priorities</option>
                {PRIORITIES.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <select
                className="border rounded-md p-2"
                value={filters.sla_breached}
                onChange={(e) => setFilters({...filters, sla_breached: e.target.value})}
              >
                <option value="">SLA Status</option>
                <option value="true">Breached</option>
                <option value="false">On Track</option>
              </select>
              <Button variant="outline" size="sm" onClick={() => setFilters({ status: '', priority: '', sla_breached: '' })}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Repair Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : repairs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No repair requests found
                  </div>
                ) : (
                  <div className="space-y-4">
                    {repairs.map((repair) => (
                      <div
                        key={repair.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedRepair?.id === repair.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                        } ${repair.sla_breached ? 'border-red-300 bg-red-50' : ''}`}
                        onClick={() => fetchRepairDetails(repair.id)}
                        data-testid={`repair-${repair.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium">{repair.title}</h3>
                              {getStatusBadge(repair.status)}
                              {getPriorityBadge(repair.priority)}
                              {repair.sla_breached && (
                                <Badge className="bg-red-500 text-white">SLA BREACHED</Badge>
                              )}
                              {repair.is_hazard && (
                                <Badge className="bg-orange-500 text-white">HAZARD</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{repair.request_number}</p>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-1">{repair.description}</p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                        
                        {/* SLA Progress */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>SLA Progress</span>
                            <span>{new Date(repair.sla_deadline).toLocaleDateString()}</span>
                          </div>
                          <Progress 
                            value={getSLAProgress(repair)} 
                            className={repair.sla_breached ? 'bg-red-100' : ''}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            {selectedRepair ? (
              <Card>
                <CardHeader>
                  <CardTitle>Repair Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Reference</p>
                    <p className="font-mono">{selectedRepair.request_number}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedRepair.status)}
                    {getPriorityBadge(selectedRepair.priority)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Category</p>
                    <p>{REPAIR_CATEGORIES.find(c => c.id === selectedRepair.category)?.label}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Description</p>
                    <p className="text-sm">{selectedRepair.description}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">SLA Deadline</p>
                    <p className={selectedRepair.sla_breached ? 'text-red-600 font-medium' : ''}>
                      {new Date(selectedRepair.sla_deadline).toLocaleString()}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-t pt-4 space-y-2">
                    {selectedRepair.status === 'reported' && (
                      <Button className="w-full" onClick={() => handleAcknowledge(selectedRepair.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Acknowledge
                      </Button>
                    )}
                    {selectedRepair.status === 'acknowledged' && (
                      <Button className="w-full" onClick={() => handleSchedule(selectedRepair.id)}>
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule
                      </Button>
                    )}
                    {['scheduled', 'in_progress'].includes(selectedRepair.status) && (
                      <Button className="w-full" onClick={() => handleComplete(selectedRepair.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark Completed
                      </Button>
                    )}
                  </div>

                  {/* Updates History */}
                  {selectedRepair.updates && selectedRepair.updates.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-slate-500 mb-2">History</p>
                      <div className="space-y-2">
                        {selectedRepair.updates.map((update, idx) => (
                          <div key={idx} className="text-sm border-l-2 border-slate-200 pl-3">
                            <p className="font-medium">{update.update_type.replace('_', ' ')}</p>
                            {update.notes && <p className="text-slate-600">{update.notes}</p>}
                            <p className="text-slate-400 text-xs">
                              {new Date(update.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  Select a repair to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
